import { useSelector } from 'react-redux';
import { Fragment } from 'react';

import { TIME_FILTER } from '@/constants/timeConstants';
import useReviews from '@/hooks/useReviews';
import { _t } from '@/locale';
import { FormState } from '@/reducers/formReducer';
import { ReviewsState } from '@/reducers/reviewsReducer';

import { ReviewsSkeleton } from '@/features/reviews/ReviewsSkeleton';
import { ReviewsHeader } from '@/features/reviews/ReviewsHeader';
import { ReviewStats } from '@/components/modules/review';
import ReviewCardV2 from '@/features/reviews/ReviewCardV2';
import { Button } from '@/components/elements/forms/buttons';

type ReviewsProps = {
  placeId: number;
  hideStaff?: boolean;
  isMpReviews?: boolean;
  uid?: string;
  originType?: 'placePage' | 'reviewsModal' | 'dealsPage';
  tooltipTargetRef?: React.RefObject<HTMLDivElement>;
  focus?: 'employee' | 'service' | 'rating';
  onEmployeeClick?: (employeeId: number, serviceId: number) => void;
  onServiceClick?: (employeeId: number, serviceId: number) => void;
  onRatingClick?: (rating: string) => void;
  onShowMoreClick?: () => void;
  onScoreTitleClick?: () => void;
};

const FALLBACK_STATE = {
  ids: [],
  newerIds: [],
  olderIds: [],
  searchResult: {
    nextPage: null,
  },
  details: {
    stats: {},
    ratingCounts: {},
    reviewCounts: {},
    employeeStats: {},
  },
};

export function Reviews(props: ReviewsProps) {
  const { uid, onEmployeeClick, onServiceClick, onRatingClick, focus, onShowMoreClick, onScoreTitleClick, originType } =
    props;

  const reviews = useSelector((state: { reviews: ReviewsState }) => state.reviews.byUid[uid] || FALLBACK_STATE);
  const searchFilter = useSelector((state: { form: FormState }) => state.form[uid]?.searchFilter);

  const { setTimeFilter, searchResult } = useReviews({
    uid,
  });

  const hasReviews = reviews?.ids && reviews.ids.length > 0;

  const getList = () => {
    if (hasReviews === false) {
      return (
        <div className="flex min-h-[140px] w-full items-center justify-center">
          <p className="text-fg_secondary">{_t('noReviewsToDisplay')}</p>
        </div>
      );
    }

    return (
      <div className="gap-md flex flex-col">
        {reviews.newerIds.map((reviewId: string) => {
          return (
            <Fragment key={reviewId}>
              <ReviewCardV2
                key={reviewId}
                reviewId={reviewId}
                onEmployeeClick={onEmployeeClick}
                onServiceClick={onServiceClick}
              />
            </Fragment>
          );
        })}
        {reviews.olderIds.length > 0 && searchFilter.timeFilter === TIME_FILTER.WITHIN_ONE_YEAR && (
          <div className="flex min-h-[60px] w-full items-center justify-center">
            <h5 className="text-h-s">{_t('components.elements.Rating.olderReviews')}</h5>
          </div>
        )}
        {reviews.olderIds.map((reviewId: string) => {
          return (
            <Fragment key={reviewId}>
              <ReviewCardV2
                key={reviewId}
                reviewId={reviewId}
                onEmployeeClick={onEmployeeClick}
                onServiceClick={onServiceClick}
              />
            </Fragment>
          );
        })}
      </div>
    );
  };

  const getReviewStats = ({
    stats,
    ratingCounts,
    reviewCounts,
  }: {
    stats: {
      score?: number;
      count?: number;
    };
    ratingCounts: any;
    reviewCounts: any;
  }) => {
    return (
      <ReviewStats
        className={'!py-0'}
        stats={stats}
        ratingCounts={ratingCounts}
        reviewCounts={reviewCounts}
        ratingFilter={searchFilter?.rating}
        minimumCount={0}
        withRatingLabel
        withEmptyState
        onTitleClick={onScoreTitleClick}
        onRatingClick={
          onRatingClick
            ? (rating: string) => {
                onRatingClick(rating);
              }
            : undefined
        }
      />
    );
  };

  const getStats = () => {
    if (!reviews?.details?.stats) {
      return null;
    }

    if (focus === 'employee') {
      const employeeId = searchFilter?.employeeId;
      const employeeStats = reviews?.details?.employeeStats?.[employeeId];

      if (!employeeStats) {
        return null;
      }

      const employeeStatsObject = {
        stats: {
          score: employeeStats.score,
          count: employeeStats.count,
        },
        ratingCounts: employeeStats?.ratingCounts,
        reviewCounts: employeeStats?.reviewCounts,
      };

      return getReviewStats(employeeStatsObject);
    }

    const serviceStatsObject = {
      stats: reviews?.details?.stats,
      ratingCounts: reviews?.details?.ratingCounts,
      reviewCounts: reviews?.details?.reviewCounts,
    };

    return getReviewStats(serviceStatsObject);
  };

  const shouldDisplaySkeleton = () => {
    if (!searchResult) {
      return true;
    }

    // Between mount and the fetch's own `loading: true` dispatch landing, `fetched` and
    // `loading` are both still false (EMPTY_SEARCH_RESULT/FALLBACK_STATE) — without this, that
    // gap reads as "no reviews to display" for a frame before the skeleton takes over.
    if (!searchResult.fetched && !searchResult.loading) {
      return true;
    }

    if (searchResult.loadingByType?.timeFilter) {
      return false;
    }

    if (searchResult.loadingByType?.loadMore) {
      return false;
    }

    if (searchResult.loading) {
      return true;
    }

    return false;
  };

  const getContent = () => {
    if (shouldDisplaySkeleton()) {
      const withStars = (() => {
        if (focus === 'rating') {
          return true;
        }

        if (focus === 'employee') {
          const employeeId = searchFilter?.employeeId;
          const employeeStats = reviews?.details?.employeeStats?.[employeeId];
          return employeeStats && !!employeeStats.count;
        }

        return false;
      })();

      const numberOfReviewsSkeletonCards = (() => {
        if (focus === 'rating') {
          return searchFilter?.limit || 3;
        }

        return Math.min(searchResult?.count || 1, searchFilter?.limit || 3);
      })();

      return (
        <ReviewsSkeleton withList ratingRows={5} reviewCards={numberOfReviewsSkeletonCards} withStars={withStars} />
      );
    }

    return (
      <>
        {getStats()}
        {getList()}
      </>
    );
  };

  const getListFooter = () => {
    if (!reviews.searchResult?.nextPage || !onShowMoreClick) {
      return null;
    }

    return (
      <div className={'w-100 flex items-center justify-center'}>
        <Button
          label={_t('showAllReviews')}
          onClick={onShowMoreClick}
          variant={originType === 'reviewsModal' ? 'secondary' : 'primary'}
        />
      </div>
    );
  };

  return (
    <div className={'gap-sm relative flex scroll-mt-[120px] flex-col'}>
      {hasReviews ? (
        <ReviewsHeader
          setTimeFilter={setTimeFilter}
          disabled={searchResult?.loading || !reviews?.ids || reviews.ids.length === 0}
          selectedTimeFilter={searchFilter?.timeFilter}
          className={'mb-lg'}
        />
      ) : null}
      <div className="gap-2xl flex flex-col">
        {getContent()}
        {getListFooter()}
      </div>
    </div>
  );
}

export default Reviews;
